import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "../../lib/api";
import { reportError } from "../../lib/errorReporter";
import { apiFieldErrors } from "../../lib/companyValidation";
import { platformErrorMessage } from "../../lib/platformValidation";
import { PURGE_GRACE_DAYS, matchesExactName } from "../../lib/purgeConfirm";
import PlatformConfirmNameField from "./PlatformConfirmNameField";

/**
 * Request permanent deletion of an ARCHIVED organization.
 *
 * Nothing is destroyed here. The organization stays archived for the grace
 * period, during which the request can be cancelled, and only then may a
 * platform administrator purge it manually. The grace period is a server-side
 * constant: this dialog cannot shorten it, and it is not part of the request
 * body.
 */
export const PlatformDeletionRequestDialog = ({
  organizationId,
  organization,
  onCancel,
  onRequested,
}) => {
  const { t } = useTranslation();
  const [typed, setTyped] = useState("");
  const [fieldError, setFieldError] = useState("");
  const [formError, setFormError] = useState("");
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);

  const name = organization?.name || "";
  const matches = matchesExactName(typed, name);

  const submit = async (event) => {
    event.preventDefault();
    if (inFlight.current || busy) return;
    if (!matches) {
      setFieldError(t("platform.confirm_name_mismatch"));
      return;
    }
    inFlight.current = true;
    setBusy(true);
    setFieldError("");
    setFormError("");
    try {
      const r = await api.post(
        `/platform-admin/organizations/${organizationId}/deletion-request`,
        { confirm_name: typed.trim() },
        { skipOrgHeader: true },
      );
      onRequested(r.data);
    } catch (err) {
      reportError("platform.requestOrganizationDeletion", err);
      const fields = apiFieldErrors(err);
      if (fields?.confirm_name) setFieldError(fields.confirm_name);
      else setFormError(platformErrorMessage(err, t("common.error_generic")));
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  return (
    <div className="qw-modal-backdrop">
      <div
        className="qw-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="platform-deletion-request-title"
        data-testid="platform-deletion-request-dialog"
      >
        <div
          className="h"
          id="platform-deletion-request-title"
          data-testid="platform-deletion-request-dialog-title"
        >
          {t("platform.deletion_request_title")}
        </div>
        <form onSubmit={submit} noValidate>
          <div className="body">
            <p className="qw-help" style={{ marginTop: 0 }} data-testid="platform-deletion-request-lead">
              {t("platform.deletion_request_lead", { days: PURGE_GRACE_DAYS })}
            </p>
            <PlatformConfirmNameField
              idPrefix="platform-deletion-request"
              name={name}
              value={typed}
              onChange={(v) => {
                setTyped(v);
                setFieldError("");
                setFormError("");
              }}
              disabled={busy}
              error={fieldError}
            />
            {formError && (
              <div className="qw-error" data-testid="platform-deletion-request-error">
                {formError}
              </div>
            )}
          </div>
          <div className="footer">
            <button
              type="button"
              className="qw-btn ghost"
              onClick={onCancel}
              disabled={busy}
              data-testid="platform-deletion-request-cancel"
            >
              {t("platform.cancel")}
            </button>
            <button
              type="submit"
              className="qw-btn danger"
              disabled={busy || !matches}
              data-testid="platform-deletion-request-submit"
            >
              {busy ? t("platform.sending") : t("platform.deletion_request_submit")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PlatformDeletionRequestDialog;
